import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from 'react-router-dom';
import styles from "./ConfigureScheduler.module.css";
import Button from "common-ui-components/components/button";
import Schedule from "common-ui-components/components/schedule/Schedule";
import API from "common-ui-components/components/utils/API";
import parseCron from "../../../common-ui-components/components/schedule/parseCron";
import {
  FormInputContainer,
  FormInputLabel,
  FormInput,
} from "app/common/form/FormInputBlock";
import MultiSelectInput from "common-ui-components/components/select";
import {
  buildIsoStringWithOffsetStr,
  isValidEmail,
} from "../utils/string-utils";
import { notify } from "common-ui-components/components/notification/Notification";
import { Failure } from "common-ui-components/components/notification/templates";
import ResponseLoader, {
  hideLoader,
  showLoader,
} from "common-ui-components/components/ResponseLoader/ResponseLoader";
import { useRestrictionChecker } from "common-ui-components/components/restrictionUI/RestrictionChecker";

const ConfigureScheduler = ({ callback, deps, home = false, isEdit = false }) => {
  const { checkRestriction } = useRestrictionChecker();
  const canSchedule = !checkRestriction("SCHEDULE_A_SCAN", home, "page");

  const [schedule, setSchedule] = useState(null);
  const [value, setValue] = useState(null);
  const [emailIds, setEmailIds] = useState([]);

  const navigate = useNavigate();

  const checkEdit = isEdit;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSchedule = async () => {
      showLoader();
      const jobId = new URLSearchParams(window.location.search).get("jobId");
      const response = await API.get(`/scheduledJob?methodId=1&jobId=${jobId}`); // configurable
      if (response.status.success) {
        const prevSchedule = parseCron(response.body.expression);
        prevSchedule.startDate = response.body.startDate;
        prevSchedule.timeZone = response.body.timeZone;
        setValue(prevSchedule);
        setEmailIds(response.body.emailIds);
      }
      hideLoader();
    };
    if (canSchedule) {
      if (checkEdit) fetchSchedule();
    }
  }, []);

  const handleScheduleChange = useCallback((newSchedule) => {
    setSchedule(newSchedule);
  }, []);

  const copyCron = (e) => {
    e.persist();
    navigator.clipboard.writeText(schedule.cron);
    e.target.innerText = "Copied!";
    setTimeout(() => {
      e.target.innerText = "Copy Cron";
    }, 1000);
  };

  const scheduleJob = async (e) => {
    e.preventDefault();
    if (checkRestriction("SCHEDULE_A_SCAN")) return;
    const timeZone = schedule.timeZone;
    const startDate = schedule.startDate;
    let response;
    try {
      setLoading(true);
      if (!checkEdit) {
        response = await API.post("/scheduleJob", {
          // configurable
          expression: schedule.cron,
          startDate,
          emailIds,
          timeZone,
          methodId: 1, // configurable - do not use the enum directly, instead use this to get enum
          serviceId: 1,
        });
        if (response.status.success) navigate(`../view`); // configurable
        else notify(<Failure>Error in getting scheduler details</Failure>);
      } else {
        const jobId = new URLSearchParams(window.location.search).get("jobId");
        response = await API.post("/updateJob", {
          // configurable
          expression: schedule.cron,
          startDate,
          emailIds,
          timeZone,
          jobId,
        });
        if (response.status.success) navigate(`../view`); // configurable
        else notify(<Failure>Error in updating scheduler details</Failure>);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (home === true) {
      window.location.reload();
    } else navigate("../view");
  };

  return (
    <div>
      {/* {loading && <ResponseLoader />} */}
      <div>
        <form
          className={styles.configureSchedulerContainer}
          style={
            home ? {} : { height: "calc(90vh - 120px)", overflowY: "auto" }
          }
        >
          <Schedule
            onChange={handleScheduleChange}
            value={checkEdit ? value : null}
          >
            <EditNotification {...{ emailIds, setEmailIds }} />
          </Schedule>
        </form>
        <div className={styles.controls}>
          <Button
            type="submit"
            className={styles.saveButton}
            onClick={scheduleJob}
            loading={loading}
          >
            {loading ? "Saving" : "Save"}
          </Button>
          <Button
            type="reset"
            className={styles.cancelBtn}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

const EditNotification = ({
  emailIds,
  setEmailIds,
  showLabel = true,
  focusOnTextArrChange = true,
  width = "35rem",
  triggerText = "Add emails",
}) => {
  const [technicianData, setTechnicianData] = useState([]);
  const [active, setActive] = useState([]);

  useEffect(() => {
    const getTechnicianData = async () => {
      try {
        const response = await API.get("/inventory/getEntityPairs?entityType=users&page=0&size=50");
        if (!response.status.success) {
          throw response;
        }
        const normalized = response.body.map(obj => {
          const [key, value] = Object.entries(obj)[0]; // grab the first [key, value] pair
          return {
            name: value,
            email: value,
          };
        });

        setTechnicianData(normalized);
      } catch (error) {
        console.error(error);
      }
    };
    getTechnicianData();
  }, []);

  useEffect(() => {
    if (!emailIds) return;

    let updatedTechData = [...technicianData];
    let listChanged = false;

    emailIds.forEach((email) => {
      const exists = updatedTechData.some((user) => user.email === email);
      if (!exists) {
        updatedTechData.push({ email, name: email });
        listChanged = true;
      }
    });

    const newActiveIndices = emailIds
      .map((email) =>
        updatedTechData.findIndex((technician) => technician.email === email)
      )
      .filter((index) => index !== -1);

    if (listChanged) {
      setTechnicianData(updatedTechData);
    }

    const isDifferent =
        newActiveIndices.length !== active.length ||
        !newActiveIndices.every((val, index) => val === active[index]);

    if (isDifferent) {
      setActive(newActiveIndices);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailIds, technicianData]);

  const syncToParent = (newActiveIndices, currentData) => {
      const selectedEmails = newActiveIndices.map(index => currentData[index].email);
      setEmailIds(selectedEmails);
  };

  const handleOptionSelect = (_option, index) => {
    let newActive;
    if (active.includes(index)) {
      newActive = active.filter((value) => value !== index);
    } else {
      newActive = [...active, index];
    }

    setActive(newActive);
    syncToParent(newActive, technicianData);
  };

  const handleResetClick = () => {
    setActive([]);
    setEmailIds([]);
  };

  const handleAddEmail = (emailType, email) => {
    if (!isValidEmail(email)) {
      notify(<Failure>Invalid Email</Failure>);
      return false;
    }

    let index = technicianData.findIndex((t) => t.email == email);
    let currentData = technicianData;

    if (index == -1) {
      const newTech = { name: email, email };
      currentData = [...technicianData, newTech];
      setTechnicianData(currentData);
      index = currentData.length - 1;
    }

    if (!active.includes(index)) {
        const newActive = [...active, index];
        setActive(newActive);
        syncToParent(newActive, currentData);
    }

    return true;
  };

  const handleRemoveEmail = (emailType, indexToDelete) => {
    const realIndex = active[indexToDelete];
    const newActive = active.filter((index) => index != realIndex);

    setActive(newActive);
    syncToParent(newActive, technicianData);
  };

  const technicians = technicianData.map((technician) => technician.name);

  return (
    <FormInputContainer
      style={{
        alignItems: "stretch",
        ...(showLabel ? {} : { width: "100%", maxWidth: "none" }),
      }}
    >
      {showLabel && (
        <FormInputLabel>
          <label>
            Email Address <span style={{ color: "red" }}>*</span>
          </label>
        </FormInputLabel>
      )}
      <FormInput
        style={showLabel ? undefined : { width: "100%", maxWidth: "none", flex: "none" }}
      >
        <div>
          <MultiSelectInput
            width={width}
            triggerProps={{
              className: styles.select,
              focusOnTextArrChange,
            }}
            triggerText={triggerText}
            handleAddOption={handleAddEmail}
            handleRemoveOption={handleRemoveEmail}
            active={active}
            setSelected={setActive}
            setActive={setActive}
            options={technicians}
            multiple={{
              optionsText: "Technicians",
              handleReset: handleResetClick,
              hideApplyButton: true,
            }}
            onOptionSelect={handleOptionSelect}
            searchWidth="25rem"
            noResultsText='No such email found. Click "Enter" to add this email.'
          />
        </div>
      </FormInput>
    </FormInputContainer>
  );
};

export { EditNotification };

export default ConfigureScheduler;
